import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Article,
  ArticleFill,
  Animation,
  AnimationFill,
  ClosedCaption,
  ClosedCaptionFill,
  Interests,
  InterestsFill,
  Forum,
  ForumFill,
  MusicNote,
  TransitionChop,
  TransitionChopFill,
  WandStars,
  WandStarsFill,
} from '@crabs-ai/web-icons';
import { EditorPropertyMenu, EditorPropertyMenuKey } from './types/types';
import ScriptContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/script/ScriptContent';
import AssetContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/asset/AssetContent';
import TransitionContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/transition/TransitionContent';
import AnimationContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/animation/AnimationContent';
import AiSceneContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-scene/AiSceneContent';
import AiVideoGenerateModal from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-video/AiVideoGenerateModal';
import AiSourceContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/ai-source/AiSourceContent';
import AudioContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/audio/AudioContent';
import { PlayerRef } from '@remotion/player';
import { useTranslations } from 'next-intl';
import CaptionsContent from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/captionns/CaptionsContent';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';
import { usePropertyPanelContext } from '@/features/remotion-editor/editor/contexts/property-panel-context';
import { DtoProjectType } from '@/shared/constants';
import { useFeatureEnabled } from '@/shared/hooks/useFeatureEnabled';
import { PropertyPanelMenuItem } from '@crabs-ai/ds-react';

interface ScriptPanelProps {
  playerRef: React.RefObject<PlayerRef | null>;
  projectId: number;
}

const PROPERTY_MENU_STORAGE_KEY = 'editor-property-menu';

type ProjectTypeValue = (typeof DtoProjectType)[keyof typeof DtoProjectType];

const getDefaultMenuByProjectType = (
  projectType: ProjectTypeValue | null,
): EditorPropertyMenuKey => {
  if (projectType === DtoProjectType.commerce) {
    return 'script';
  }

  if (projectType === DtoProjectType.generateToShort) {
    return 'asset';
  }

  return 'caption';
};

function EditorPropertyPanel({ playerRef, projectId }: ScriptPanelProps) {
  const t = useTranslations();

  const { projectType } = useProjectTypeContext();
  const { activeMenu, setActiveMenu, aiVideoModalState, setAiVideoModalState } =
    usePropertyPanelContext();
  const { enable } = useFeatureEnabled();

  const propertyMenus = useMemo((): EditorPropertyMenu[] => {
    return [
      {
        key: 'script',
        label: t('Editor.property-menu.scene'),
        tooltip: t('Editor.property-menu-tooltip.scene'),
        iconNode: (
          <Article className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <ArticleFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: projectType === DtoProjectType.commerce,
      },
      {
        key: 'caption',
        label: t('Editor.property-menu.captions'),
        tooltip: t('Editor.property-menu-tooltip.captions'),
        iconNode: (
          <ClosedCaption className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <ClosedCaptionFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: projectType !== DtoProjectType.generateToShort,
      },
      {
        key: 'asset',
        label: t('Editor.property-menu.asset'),
        tooltip: t('Editor.property-menu-tooltip.asset'),
        iconNode: (
          <Interests className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <InterestsFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: true,
      },
      {
        key: 'ai-source',
        label: t('Editor.property-menu.ai-source'),
        tooltip: t('Editor.property-menu-tooltip.ai-source'),
        iconNode: (
          <WandStars className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <WandStarsFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: true,
      },
      {
        key: 'transition',
        label: t('Editor.property-menu.transition'),
        tooltip: t('Editor.property-menu-tooltip.transition'),
        iconNode: (
          <TransitionChop className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <TransitionChopFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: true,
      },
      {
        key: 'effect',
        label: t('Editor.property-menu.animation'),
        tooltip: t('Editor.property-menu-tooltip.animation'),
        iconNode: (
          <Animation className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <AnimationFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: true,
      },
      {
        key: 'sound-effect',
        // 내부 key는 기존 localStorage 복원 호환성을 위해 유지하고,
        // 사용자에게만 '오디오' 메뉴로 노출한다.
        label: t('Editor.property-menu.audio'),
        tooltip: t('Editor.property-menu-tooltip.audio'),
        iconNode: (
          <MusicNote className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <MusicNote className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: true,
      },
      {
        key: 'ai-scene',
        label: t('Editor.property-menu.ai-scene'),
        tooltip: t('Editor.property-menu-tooltip.ai-scene'),
        iconNode: (
          <Forum className="h-6 w-6 transition-all [&_path]:fill-sys-cont-neutral-default" />
        ),
        selectedIconNode: (
          <ForumFill className="h-6 w-6 transition-all [&_path]:fill-sys-cont-primary-default" />
        ),
        isVisible: enable,
      },
    ];
  }, [projectType, t, enable]);

  const visiblePropertyMenus = useMemo(() => {
    // 현재 프로젝트/feature flag 조건에서 실제로 노출 가능한 메뉴만 사용한다.
    return propertyMenus.filter((item) => item.isVisible);
  }, [propertyMenus]);

  const visibleMenuKeys = useMemo(() => {
    return visiblePropertyMenus.map((item) => item.key);
  }, [visiblePropertyMenus]);

  const normalizedActiveMenu = useMemo(() => {
    if (!activeMenu) {
      return null;
    }

    // 기존 activeMenu가 더 이상 보이지 않는 메뉴라면 렌더링 대상에서 제외한다.
    return visibleMenuKeys.includes(activeMenu) ? activeMenu : null;
  }, [activeMenu, visibleMenuKeys]);

  const handleAiVideoModalClose = useCallback(() => {
    setAiVideoModalState(null);
  }, [setAiVideoModalState]);

  const handleAiVideoModalGenerateSuccess = useCallback(() => {
    setAiVideoModalState(null);
    setActiveMenu('ai-source');
  }, [setAiVideoModalState, setActiveMenu]);

  useEffect(() => {
    if (projectType === null) {
      return;
    }

    const savedMenu =
      typeof window !== 'undefined'
        ? localStorage.getItem(PROPERTY_MENU_STORAGE_KEY)
        : null;

    const visibleSavedMenu =
      savedMenu === null
        ? undefined
        : visibleMenuKeys.find((key) => key === savedMenu);

    const defaultMenu = getDefaultMenuByProjectType(projectType);
    // 복원 메뉴 -> 프로젝트 기본 메뉴 -> 첫 번째 노출 메뉴 순으로 fallback.
    const nextMenu =
      visibleSavedMenu ??
      (visibleMenuKeys.includes(defaultMenu)
        ? defaultMenu
        : (visibleMenuKeys[0] ?? null));

    setActiveMenu(nextMenu);

    if (typeof window !== 'undefined') {
      if (nextMenu) {
        localStorage.setItem(PROPERTY_MENU_STORAGE_KEY, nextMenu);
      } else {
        localStorage.removeItem(PROPERTY_MENU_STORAGE_KEY);
      }
    }
  }, [projectType, setActiveMenu, visibleMenuKeys]);

  return (
    <section className="flex h-full w-full bg-sys-bg-surface">
      <aside className="flex w-auto shrink-0 flex-col overflow-y-auto border-r border-r-sys-style-neutral-default p-2xs">
        <nav className="flex flex-col gap-2xs">
          {visiblePropertyMenus.map(
            ({ key, label, tooltip, iconNode, selectedIconNode }) => {
              const isSelected = normalizedActiveMenu === key;

              return (
                <PropertyMenuItem
                  key={key}
                  text={label}
                  iconNode={iconNode}
                  selectedIconNode={selectedIconNode}
                  isSelected={isSelected}
                  tooltip={tooltip ?? label}
                  onClick={() => {
                    setActiveMenu(key);
                    localStorage.setItem(PROPERTY_MENU_STORAGE_KEY, key);
                  }}
                />
              );
            },
          )}
        </nav>
      </aside>
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        {normalizedActiveMenu === 'script' &&
          projectType === DtoProjectType.commerce && (
            <ScriptContent playerRef={playerRef} />
          )}
        {normalizedActiveMenu === 'caption' && (
          <CaptionsContent playerRef={playerRef} />
        )}
        {normalizedActiveMenu === 'asset' && (
          <AssetContent
            playerRef={playerRef}
            projectId={projectId}
          />
        )}
        {normalizedActiveMenu === 'ai-source' && (
          <AiSourceContent
            projectId={projectId}
            playerRef={playerRef}
          />
        )}
        {normalizedActiveMenu === 'transition' && <TransitionContent />}
        {normalizedActiveMenu === 'effect' && <AnimationContent />}
        {normalizedActiveMenu === 'sound-effect' && (
          // 효과음과 배경음악을 같은 메뉴 안에서 탭으로 분기한다.
          <AudioContent playerRef={playerRef} />
        )}
        {normalizedActiveMenu === 'ai-scene' && (
          <AiSceneContent projectId={projectId} />
        )}
      </div>

      {/* AI 동영상 생성 모달 (소스 패널에서 열림) */}
      <AiVideoGenerateModal
        isOpen={aiVideoModalState?.isOpen ?? false}
        onClose={handleAiVideoModalClose}
        imageUrl={aiVideoModalState?.imageUrl ?? ''}
        projectId={projectId}
        onGenerateSuccess={handleAiVideoModalGenerateSuccess}
      />
    </section>
  );
}

function PropertyMenuItem({
  text,
  iconNode,
  selectedIconNode,
  isSelected,
  tooltip,
  onClick,
}: {
  text: string;
  iconNode: React.ReactNode;
  selectedIconNode?: React.ReactNode;
  isSelected: boolean;
  tooltip?: React.ReactNode | boolean;
  onClick?: () => void;
}) {
  return (
    <PropertyPanelMenuItem
      text={text}
      iconNode={iconNode}
      selectedIconNode={selectedIconNode}
      isSelected={isSelected}
      tooltip={tooltip}
      onClick={onClick}
      className="size-full min-h-16"
    />
  );
}

export default EditorPropertyPanel;
